import type { FragmentType } from 'src/gql';
import { addMonths } from 'date-fns';
import { TASK_NAMES } from 'src/constants/tasks';
import { STATE_CODE_TO_NAME } from 'src/constants/us-states';
import { graphql, unmaskFragment } from 'src/gql';
import { maskedAccountNumber } from 'src/utils/account';

import type { AlertTypeName } from '@savvy/constants/generated/gql-enums';
import { TASK_TYPE_FILE_UPLOAD } from '@savvy/constants/task-types';
import defaultGuard from '@savvy/utils/default-guard';

import { formatDateToYYYYMMDD } from './formatting';

// The Canvas route accepts a document target only for this notification source and re-authorizes it server-side.
export const MEETING_PREP_NOTIFICATION_SOURCE = 'meeting_prep_notification';

export const LeadEngagementAlertTypes: AlertTypeName[] = [
  'email_from_lead',
  'sms_from_lead',
  'outbound_missed_call_reminder',
  'call_step_required',
  'inbound_missed_call',
  'hubspot_lead_created',
];

export const ServiceCalendarAlertTypes: AlertTypeName[] = ['service_calendar_setup', 'service_calendar_due_date'];

export const TaxAlertTypes: AlertTypeName[] = [
  'tax_filing_ready',
  'federal_return_accepted',
  'federal_return_received',
  'federal_return_rejected',
  'state_return_accepted',
  'state_return_received',
  'state_return_rejected',
];

export const PortfolioAlertTypes: AlertTypeName[] = [
  'allocation_variance',
  'cash_variance',
  'rmds',
  'quarterly_report_failed',
];

export function getStateNameFromCode(stateCode: string): string {
  return STATE_CODE_TO_NAME[stateCode] ?? stateCode;
}

export const UNATTACHED_ALERT_DISPLAY_NAME = 'Unattached meeting';

export const GetAlertDisplayNameFragment = graphql(/* GraphQL */ `
  fragment GetAlertDisplayName on Alert {
    typeOf
    subject
    household {
      name
      taxEnrollments {
        enrollmentType
      }
    }
  }
`);

// Archived rows, snoozed rows, and the activity row all read this in the household slot, so the
// fallback has to produce something nameable for an alert that resolves no household.
export function getAlertDisplayName(alertFrag: FragmentType<typeof GetAlertDisplayNameFragment>): string {
  const alert = unmaskFragment(GetAlertDisplayNameFragment, alertFrag);
  return alert.household?.name ?? alert.subject ?? UNATTACHED_ALERT_DISPLAY_NAME;
}

export function alertDateFormatted(date: Date) {
  if (date.getUTCFullYear() === new Date().getUTCFullYear()) {
    return date.toLocaleDateString('en-us', {
      month: 'short',
      day: 'numeric',
    });
  }
  return date.toLocaleDateString('en-us', {
    dateStyle: 'short',
  });
}

export function getAlertChipText(alertType: AlertTypeName): string {
  switch (alertType) {
    case 'outstanding_client_task':
      return 'Incomplete client task';
    case 'overdue_advisor_task':
      return 'Advisor task due';
    case 'allocation_variance':
      return 'Allocation variance';
    case 'client_engagement':
      return 'Client inactivity';
    case 'stagnant_prospect':
      return 'Prospect inactivity';
    case 'cash_variance':
      return 'Cash allocation';
    case 'advisor_requirements_needed':
      return 'Advisor input';
    case 'transfer_in':
      return 'Transfer in';
    case 'birthdays':
      return 'Birthday';
    case 'email_from_lead':
    case 'sms_from_lead':
    case 'outbound_missed_call_reminder':
    case 'call_step_required':
    case 'inbound_missed_call':
    case 'new_lead_campaign_started':
    case 'hubspot_lead_created':
      return 'Engage with lead';
    case 'service_calendar_setup':
    case 'service_calendar_due_date':
      return 'Service calendar';
    case 'rmds':
      return 'RMDs';
    case 'client_logins':
      return 'Client Logins';
    case 'quarterly_report_failed':
      return 'Quarterly report failed';
    case 'quarterly_report_generated':
      return 'Quarterly report generated';
    case 'task_reassigned':
      return 'Task assigned to you';
    case 'task_status_changed':
      return 'Status change';
    case 'task_comment_created':
      return 'Comment';
    case 'task_comment_mention':
    case 'household_comment_mention':
    case 'center_of_influence_rep_comment_mention':
      return 'Mention';
    case 'subtask_assigned':
    case 'subtask_due_approaching':
      return 'Task';
    case 'tax_filing_ready':
    case 'state_return_rejected':
    case 'state_return_accepted':
    case 'state_return_received':
    case 'federal_return_rejected':
    case 'federal_return_accepted':
    case 'federal_return_received':
    case 'client_completed_tax_enrollment':
      return 'Tax filing';
    case 'client_uploaded_requested_file':
      return 'File uploaded';
    case 'client_submitted_risk_questionnaire':
      return 'Risk questionnaire';
    case 'onboarding_complete':
      return 'Onboarding';
    case 'service_calendar_task_assigned':
      return 'Service calendar';
    case 'account_opened_or_funded':
      return 'Account opened';
    case 'client_portal_message_received':
      return 'Portal message';
    case 'task_created':
    case 'client_completed_task':
    case 'task_due_date_changed':
      return 'Task';
    case 'intake_invite_sent':
    case 'intake_invite_reminder_sent':
    case 'onboarding_invite_sent':
    case 'onboarding_invite_reminder_sent':
    case 'account_setup_confirmation_sent':
    case 'file_upload_request_sent':
    case 'risk_questionnaire_sent':
    case 'pre_review_check_in_sent':
      return 'Onboarding';
    case 'service_calendar_invite_sent':
    case 'service_calendar_booking_reminder_sent':
    case 'meeting_booked_by_client':
    case 'meeting_canceled_or_rescheduled_by_client':
    case 'service_calendar_upcoming_meeting':
      return 'Service calendar';
    case 'tax_filing_ready_sent_to_client':
    case 'tax_filing_reminder_to_client':
    case 'tax_filing_late_or_missed_deadline':
      return 'Tax filing';
    case 'account_linked_or_aggregation_added':
    case 'large_withdrawal_detected':
    case 'beneficiary_or_ownership_change':
    case 'contribution_received':
      return 'Account';
    case 'docusign_outstanding':
      return 'DocuSign';
    case 'meeting_prep_ready':
      return 'Meeting prep';
    case 'meeting_recap_ready':
      return 'Meeting recap';
    case 'meeting_transcript_needs_household':
      return 'Transcript';
    default:
      return defaultGuard(alertType, '');
  }
}

type AlertChipColor = 'green' | 'pink' | 'orange' | 'sky' | 'violet' | 'teal';

const ALERT_CHIP_COLOR: Record<AlertTypeName, AlertChipColor> = {
  outstanding_client_task: 'green',
  overdue_advisor_task: 'pink',
  service_calendar_setup: 'pink',
  service_calendar_due_date: 'pink',
  rmds: 'pink',
  task_reassigned: 'pink',
  task_status_changed: 'pink',
  task_comment_created: 'pink',
  task_comment_mention: 'pink',
  household_comment_mention: 'pink',
  center_of_influence_rep_comment_mention: 'pink',
  subtask_assigned: 'pink',
  subtask_due_approaching: 'pink',
  advisor_requirements_needed: 'pink',
  allocation_variance: 'orange',
  cash_variance: 'orange',
  transfer_in: 'orange',
  client_logins: 'sky',
  birthdays: 'sky',
  quarterly_report_failed: 'sky',
  quarterly_report_generated: 'sky',
  email_from_lead: 'violet',
  sms_from_lead: 'violet',
  outbound_missed_call_reminder: 'violet',
  call_step_required: 'violet',
  inbound_missed_call: 'violet',
  new_lead_campaign_started: 'violet',
  hubspot_lead_created: 'violet',
  stagnant_prospect: 'violet',
  client_engagement: 'violet',
  tax_filing_ready: 'teal',
  state_return_rejected: 'teal',
  state_return_accepted: 'teal',
  state_return_received: 'teal',
  federal_return_rejected: 'teal',
  federal_return_accepted: 'teal',
  federal_return_received: 'teal',
  client_completed_tax_enrollment: 'teal',
  client_uploaded_requested_file: 'green',
  client_submitted_risk_questionnaire: 'green',
  onboarding_complete: 'green',
  service_calendar_task_assigned: 'pink',
  account_opened_or_funded: 'orange',
  client_portal_message_received: 'sky',
  task_created: 'pink',
  client_completed_task: 'pink',
  task_due_date_changed: 'pink',
  intake_invite_sent: 'green',
  intake_invite_reminder_sent: 'green',
  onboarding_invite_sent: 'green',
  onboarding_invite_reminder_sent: 'green',
  account_setup_confirmation_sent: 'green',
  file_upload_request_sent: 'green',
  risk_questionnaire_sent: 'green',
  pre_review_check_in_sent: 'green',
  service_calendar_invite_sent: 'pink',
  service_calendar_booking_reminder_sent: 'pink',
  meeting_booked_by_client: 'pink',
  meeting_canceled_or_rescheduled_by_client: 'pink',
  service_calendar_upcoming_meeting: 'pink',
  tax_filing_ready_sent_to_client: 'teal',
  tax_filing_reminder_to_client: 'teal',
  tax_filing_late_or_missed_deadline: 'teal',
  account_linked_or_aggregation_added: 'orange',
  large_withdrawal_detected: 'orange',
  beneficiary_or_ownership_change: 'orange',
  docusign_outstanding: 'orange',
  contribution_received: 'orange',
  meeting_prep_ready: 'sky',
  meeting_recap_ready: 'sky',
  meeting_transcript_needs_household: 'sky',
};

const ALERT_CHIP_CLASSES: Record<AlertChipColor, string> = {
  green: 'bg-[rgba(74,222,128,0.12)] border-[rgba(74,222,128,0.35)] dark:bg-[rgba(74,222,128,0.15)]',
  pink: 'bg-[rgba(236,72,153,0.12)] border-[rgba(236,72,153,0.35)] dark:bg-[rgba(236,72,153,0.15)]',
  orange: 'bg-[rgba(249,115,22,0.12)] border-[rgba(249,115,22,0.35)] dark:bg-[rgba(249,115,22,0.15)]',
  sky: 'bg-[rgba(56,189,248,0.12)] border-[rgba(56,189,248,0.35)] dark:bg-[rgba(56,189,248,0.15)]',
  violet: 'bg-[rgba(167,139,250,0.12)] border-[rgba(167,139,250,0.35)] dark:bg-[rgba(167,139,250,0.15)]',
  teal: 'bg-[rgba(45,212,191,0.12)] border-[rgba(45,212,191,0.35)] dark:bg-[rgba(45,212,191,0.15)]',
};

export function getAlertChipClasses(alertType: AlertTypeName) {
  return ALERT_CHIP_CLASSES[ALERT_CHIP_COLOR[alertType]];
}

export function getReminderDate(num: number, period: string) {
  const date = new Date();
  if (period === 'day') date.setDate(date.getDate() + num);
  // addMonths clamps to the last day of the target month; setMonth() would roll over instead, so
  // snoozing for "1 month" on 31 January skipped February and landed on 3 March.
  if (period === 'month') return addMonths(date, num);
  return date;
}

export const GetAlertContentFragment = graphql(/* GraphQL */ `
  fragment GetAlertContent on Alert {
    typeOf
    content
    actorVerb
    relatedEntity {
      __typename
      ... on Household {
        name
      }
      ... on Task {
        type
        name
        createdAt
        dueAt
      }
      ... on StateFiling {
        stateCode
      }
      ... on Account {
        mask
      }
    }
  }
`);

type GetAlertContentOptions = {
  readonly useActorLedContent?: boolean;
};

export function getAlertContent(
  alertFrag: FragmentType<typeof GetAlertContentFragment>,
  options: GetAlertContentOptions = {},
) {
  const alert = unmaskFragment(GetAlertContentFragment, alertFrag);
  const taskName = alert.relatedEntity.__typename === 'Task' ? alert.relatedEntity.name : null;

  switch (alert.typeOf) {
    case 'outstanding_client_task':
      return (
        alert.relatedEntity.__typename === 'Task' &&
        `${TASK_NAMES[alert.relatedEntity.type]} task sent to client on ${alertDateFormatted(
          new Date(alert.relatedEntity.createdAt),
        )} ${alert.relatedEntity.type === TASK_TYPE_FILE_UPLOAD ? ` - ${alert.relatedEntity.name}` : ''}`
      );
    case 'stagnant_prospect':
      return 'Last contact 14 days ago or more and no change of stage';
    case 'state_return_accepted':
      return alert.relatedEntity.__typename === 'StateFiling'
        ? `${getStateNameFromCode(alert.relatedEntity.stateCode)} state tax return accepted`
        : alert.content;
    case 'state_return_received':
      return alert.relatedEntity.__typename === 'StateFiling'
        ? `${getStateNameFromCode(alert.relatedEntity.stateCode)} state tax return received`
        : alert.content;
    case 'state_return_rejected':
      return alert.relatedEntity.__typename === 'StateFiling'
        ? `${getStateNameFromCode(alert.relatedEntity.stateCode)} state tax return rejected`
        : alert.content;
    case 'cash_variance':
    case 'advisor_requirements_needed':
    case 'transfer_in': {
      if (alert.relatedEntity.__typename !== 'Account') return alert.content;
      const masked = maskedAccountNumber(alert.relatedEntity.mask);
      return masked ? `${alert.content} ${masked}` : alert.content;
    }

    case 'allocation_variance':
    case 'birthdays':
    case 'call_step_required':
    case 'client_engagement':
    case 'client_logins':
    case 'email_from_lead':
    case 'federal_return_accepted':
    case 'federal_return_received':
    case 'federal_return_rejected':
    case 'hubspot_lead_created':
    case 'inbound_missed_call':
    case 'new_lead_campaign_started':
    case 'outbound_missed_call_reminder':
    case 'quarterly_report_failed':
    case 'quarterly_report_generated':
    case 'rmds':
    case 'service_calendar_due_date':
    case 'service_calendar_setup':
    case 'sms_from_lead':
    case 'subtask_assigned':
    case 'subtask_due_approaching':
    case 'tax_filing_ready':
      return alert.content;
    case 'overdue_advisor_task':
      return taskName === null ? alert.content : taskName;
    case 'task_status_changed':
    case 'task_comment_created':
      return alert.content;
    case 'task_reassigned':
      return getTaskReassignedContent({ content: alert.content, taskName });
    case 'task_comment_mention':
      if (options.useActorLedContent === true && typeof alert.actorVerb === 'string') return alert.content;
      return getTaskCommentMentionContent({ content: alert.content, taskName });
    case 'household_comment_mention':
    case 'center_of_influence_rep_comment_mention':
    case 'account_opened_or_funded':
    case 'client_completed_tax_enrollment':
    case 'client_portal_message_received':
    case 'client_submitted_risk_questionnaire':
    case 'client_uploaded_requested_file':
    case 'onboarding_complete':
    case 'service_calendar_task_assigned':
      return alert.content;
    case 'task_created':
    case 'client_completed_task':
    case 'task_due_date_changed':
    case 'intake_invite_sent':
    case 'intake_invite_reminder_sent':
    case 'onboarding_invite_sent':
    case 'onboarding_invite_reminder_sent':
    case 'account_setup_confirmation_sent':
    case 'file_upload_request_sent':
    case 'risk_questionnaire_sent':
    case 'pre_review_check_in_sent':
    case 'service_calendar_invite_sent':
    case 'service_calendar_booking_reminder_sent':
    case 'meeting_booked_by_client':
    case 'meeting_canceled_or_rescheduled_by_client':
    case 'service_calendar_upcoming_meeting':
    case 'tax_filing_ready_sent_to_client':
    case 'tax_filing_reminder_to_client':
    case 'tax_filing_late_or_missed_deadline':
    case 'account_linked_or_aggregation_added':
    case 'large_withdrawal_detected':
    case 'beneficiary_or_ownership_change':
    case 'contribution_received':
    case 'docusign_outstanding':
    case 'meeting_prep_ready':
    case 'meeting_recap_ready':
    case 'meeting_transcript_needs_household':
      return alert.content;
    default:
      return defaultGuard(alert.typeOf, '');
  }
}

function getTaskReassignedContent({
  content,
  taskName,
}: {
  readonly content: string;
  readonly taskName: string | null;
}) {
  return formatTaskAlertContent({
    content,
    taskName,
    extractPrefix: extractTaskReassignedPrefix,
    defaultPrefix: 'Task assigned to you',
    separator: ': ',
  });
}

function getTaskCommentMentionContent({
  content,
  taskName,
}: {
  readonly content: string;
  readonly taskName: string | null;
}) {
  return formatTaskAlertContent({
    content,
    taskName,
    extractPrefix: extractTaskCommentMentionPrefix,
    defaultPrefix: 'You were mentioned in a comment about the task',
    separator: ' ',
  });
}

function formatTaskAlertContent({
  content,
  taskName,
  extractPrefix,
  defaultPrefix,
  separator,
}: {
  readonly content: string;
  readonly taskName: string | null;
  readonly extractPrefix: (content: string) => string | null;
  readonly defaultPrefix: string;
  readonly separator: ': ' | ' ';
}) {
  if (taskName === null) return content;

  const prefix = extractPrefix(content) ?? defaultPrefix;
  return `${prefix}${separator}${taskName}`;
}

function extractTaskReassignedPrefix(content: string) {
  const separatorIndex = content.lastIndexOf(':');
  if (separatorIndex < 0) return null;
  return content.slice(0, separatorIndex).trim();
}

function extractTaskCommentMentionPrefix(content: string) {
  const mentionSuffix = ' about the task ';
  const separatorIndex = content.lastIndexOf(mentionSuffix);
  if (separatorIndex < 0) return null;
  return content.slice(0, separatorIndex + mentionSuffix.length).trim();
}

export const GetAlertLinkFragment = graphql(/* GraphQL */ `
  fragment GetAlertLink on Alert {
    typeOf
    targetCanvasDocumentId
    relatedEntity {
      __typename
      ... on Task {
        salesforceTaskId
      }
      ... on Account {
        id
      }
      ... on CenterOfInfluenceRep {
        id
      }
    }
    household {
      salesforceAccountId
      personalPortfolio {
        id
      }
    }
  }
`);

export function getAlertLink(alertFrag: FragmentType<typeof GetAlertLinkFragment>): string {
  const alert = unmaskFragment(GetAlertLinkFragment, alertFrag);
  const { typeOf, household } = alert;
  // Resolved ahead of the household guard: a professional contact is advisor-owned, so this
  // alert deliberately carries no household, and the guard below would strand it on ''.
  if (typeOf === 'center_of_influence_rep_comment_mention') {
    return alert.relatedEntity.__typename === 'CenterOfInfluenceRep'
      ? `/advisor/dashboard/professional-contacts/${alert.relatedEntity.id}?contactTab=comments`
      : '';
  }
  // Every destination below is a household route, so an alert that resolves no household has
  // nowhere to navigate — it resolves in place on its own card instead.
  if (household === null) return '';
  switch (typeOf) {
    case 'outstanding_client_task':
      return alert.relatedEntity.__typename === 'Task'
        ? `/advisor/dashboard/client/${household.salesforceAccountId}?taskId=${alert.relatedEntity.salesforceTaskId}`
        : '';
    case 'overdue_advisor_task':
      // Check if there's a task in the related entity or if we can construct a task link
      if (alert.relatedEntity.__typename === 'Task' && alert.relatedEntity.salesforceTaskId) {
        return `/advisor/dashboard/client/${household.salesforceAccountId}?taskId=${alert.relatedEntity.salesforceTaskId}`;
      }
      // Fallback to household overview
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'stagnant_prospect':
    case 'client_engagement':
    case 'birthdays':
    case 'client_logins':
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'allocation_variance':
      return `/advisor/dashboard/client/${household.salesforceAccountId}/portfolio/${household.personalPortfolio?.id}`;
    case 'cash_variance':
    case 'transfer_in':
      return alert.relatedEntity.__typename === 'Account'
        ? `/advisor/dashboard/client/${household.salesforceAccountId}/portfolio/account/${alert.relatedEntity.id}`
        : '';
    case 'advisor_requirements_needed':
      if (alert.relatedEntity.__typename === 'Task' && alert.relatedEntity.salesforceTaskId) {
        return `/advisor/dashboard/tasks?taskId=${alert.relatedEntity.salesforceTaskId}`;
      }
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'service_calendar_setup':
    case 'service_calendar_due_date':
      return `/advisor/dashboard/client/${household.salesforceAccountId}/service-calendar`;
    case 'inbound_missed_call':
    case 'call_step_required':
    case 'email_from_lead':
    case 'outbound_missed_call_reminder':
    case 'sms_from_lead':
    case 'new_lead_campaign_started':
    case 'hubspot_lead_created':
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'rmds':
      return `/advisor/dashboard/rmds`;
    case 'quarterly_report_failed':
    case 'quarterly_report_generated':
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'task_reassigned':
    case 'task_status_changed':
    case 'task_comment_created':
    case 'task_comment_mention':
    case 'subtask_assigned':
    case 'subtask_due_approaching':
      if (alert.relatedEntity.__typename === 'Task' && alert.relatedEntity.salesforceTaskId) {
        return `/advisor/dashboard/client/${household.salesforceAccountId}?taskId=${alert.relatedEntity.salesforceTaskId}`;
      }
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'household_comment_mention':
      return `/advisor/dashboard/client/${household.salesforceAccountId}?activityTab=Comments`;
    case 'tax_filing_ready':
    case 'state_return_rejected':
    case 'state_return_accepted':
    case 'state_return_received':
    case 'federal_return_rejected':
    case 'federal_return_accepted':
    case 'federal_return_received':
    case 'client_completed_tax_enrollment':
      return `/advisor/dashboard/client/${household.salesforceAccountId}/tax`;
    case 'service_calendar_task_assigned':
    case 'client_uploaded_requested_file':
    case 'client_submitted_risk_questionnaire':
    case 'onboarding_complete':
      if (alert.relatedEntity.__typename === 'Task' && alert.relatedEntity.salesforceTaskId) {
        return `/advisor/dashboard/client/${household.salesforceAccountId}?taskId=${alert.relatedEntity.salesforceTaskId}`;
      }
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'account_opened_or_funded':
      return alert.relatedEntity.__typename === 'Account'
        ? `/advisor/dashboard/client/${household.salesforceAccountId}/portfolio/account/${alert.relatedEntity.id}`
        : `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'client_portal_message_received':
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'docusign_outstanding':
      return '/advisor/dashboard/tasks/docusign-tracker';
    case 'task_created':
    case 'client_completed_task':
    case 'task_due_date_changed':
      if (alert.relatedEntity.__typename === 'Task' && alert.relatedEntity.salesforceTaskId) {
        return `/advisor/dashboard/client/${household.salesforceAccountId}?taskId=${alert.relatedEntity.salesforceTaskId}`;
      }
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'intake_invite_sent':
    case 'intake_invite_reminder_sent':
    case 'onboarding_invite_sent':
    case 'onboarding_invite_reminder_sent':
    case 'account_setup_confirmation_sent':
    case 'file_upload_request_sent':
    case 'risk_questionnaire_sent':
    case 'pre_review_check_in_sent':
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'service_calendar_invite_sent':
    case 'service_calendar_booking_reminder_sent':
    case 'meeting_booked_by_client':
    case 'meeting_canceled_or_rescheduled_by_client':
    case 'service_calendar_upcoming_meeting':
      return `/advisor/dashboard/client/${household.salesforceAccountId}/service-calendar`;
    case 'tax_filing_ready_sent_to_client':
    case 'tax_filing_reminder_to_client':
    case 'tax_filing_late_or_missed_deadline':
      return `/advisor/dashboard/client/${household.salesforceAccountId}/tax`;
    case 'account_linked_or_aggregation_added':
    case 'large_withdrawal_detected':
    case 'beneficiary_or_ownership_change':
    case 'contribution_received':
      return `/advisor/dashboard/client/${household.salesforceAccountId}`;
    case 'meeting_prep_ready':
    case 'meeting_recap_ready': {
      const householdCanvasPath = `/advisor/canvas/household/${household.salesforceAccountId}`;
      return alert.targetCanvasDocumentId !== null
        ? `${householdCanvasPath}?canvasDocumentId=${encodeURIComponent(alert.targetCanvasDocumentId)}&source=${MEETING_PREP_NOTIFICATION_SOURCE}`
        : `${householdCanvasPath}?source=${MEETING_PREP_NOTIFICATION_SOURCE}`;
    }
    // Resolves in place on its own card rather than navigating anywhere.
    case 'meeting_transcript_needs_household':
      return '';
    default:
      return defaultGuard(typeOf, '');
  }
}

// Recaps have no background_meeting_prep_generations row to stamp, so they resolve
// to null and record no first-engagement.
export function getMeetingPrepTargetCanvasDocumentId(
  alertFrag: FragmentType<typeof GetAlertLinkFragment>,
): string | null {
  const alert = unmaskFragment(GetAlertLinkFragment, alertFrag);
  return alert.typeOf === 'meeting_prep_ready' ? alert.targetCanvasDocumentId : null;
}

export const GenerateTitleForGoogleCalendarEventFragment = graphql(/* GraphQL */ `
  fragment GenerateTitleForGoogleCalendarEvent on Alert {
    typeOf
    household {
      name
    }
  }
`);

export function generateTitleForGoogleCalendarEvent(
  alertFrag: FragmentType<typeof GenerateTitleForGoogleCalendarEventFragment>,
): string {
  const alert = unmaskFragment(GenerateTitleForGoogleCalendarEventFragment, alertFrag);
  const householdName = alert.household?.name ?? UNATTACHED_ALERT_DISPLAY_NAME;
  switch (alert.typeOf) {
    case 'outbound_missed_call_reminder':
    case 'call_step_required':
      return `Call ${householdName}`;
    case 'inbound_missed_call':
      return `Call ${householdName} back`;
    case 'email_from_lead':
    case 'sms_from_lead':
      return `Respond to ${householdName}`;
    case 'allocation_variance':
    case 'birthdays':
    case 'cash_variance':
    case 'client_engagement':
    case 'client_logins':
    case 'federal_return_accepted':
    case 'federal_return_received':
    case 'federal_return_rejected':
    case 'hubspot_lead_created':
    case 'new_lead_campaign_started':
    case 'outstanding_client_task':
    case 'overdue_advisor_task':
    case 'quarterly_report_failed':
    case 'quarterly_report_generated':
    case 'rmds':
    case 'service_calendar_due_date':
    case 'service_calendar_setup':
    case 'stagnant_prospect':
    case 'state_return_accepted':
    case 'state_return_received':
    case 'state_return_rejected':
    case 'task_reassigned':
    case 'task_status_changed':
    case 'task_comment_created':
    case 'task_comment_mention':
    case 'household_comment_mention':
    case 'center_of_influence_rep_comment_mention':
    case 'subtask_assigned':
    case 'subtask_due_approaching':
    case 'tax_filing_ready':
    case 'advisor_requirements_needed':
    case 'transfer_in':
    case 'account_opened_or_funded':
    case 'client_completed_tax_enrollment':
    case 'client_portal_message_received':
    case 'client_submitted_risk_questionnaire':
    case 'client_uploaded_requested_file':
    case 'onboarding_complete':
    case 'service_calendar_task_assigned':
    case 'task_created':
    case 'client_completed_task':
    case 'task_due_date_changed':
    case 'intake_invite_sent':
    case 'intake_invite_reminder_sent':
    case 'onboarding_invite_sent':
    case 'onboarding_invite_reminder_sent':
    case 'account_setup_confirmation_sent':
    case 'file_upload_request_sent':
    case 'risk_questionnaire_sent':
    case 'pre_review_check_in_sent':
    case 'service_calendar_invite_sent':
    case 'service_calendar_booking_reminder_sent':
    case 'meeting_booked_by_client':
    case 'meeting_canceled_or_rescheduled_by_client':
    case 'service_calendar_upcoming_meeting':
    case 'tax_filing_ready_sent_to_client':
    case 'tax_filing_reminder_to_client':
    case 'tax_filing_late_or_missed_deadline':
    case 'account_linked_or_aggregation_added':
    case 'large_withdrawal_detected':
    case 'beneficiary_or_ownership_change':
    case 'contribution_received':
    case 'docusign_outstanding':
    case 'meeting_prep_ready':
    case 'meeting_recap_ready':
    case 'meeting_transcript_needs_household':
    default:
      return '';
  }
}

export function generateDescriptionForGoogleCalendarEvent(
  alertType: AlertTypeName,
  salesforceAccountId: string,
): string {
  const ctaLink = `<a href="https://app.savvywealth.com/advisor/dashboard/client/${salesforceAccountId}">Click here to view your lead.</a>`;
  switch (alertType) {
    case 'outbound_missed_call_reminder':
      return `Try calling your lead again. ${ctaLink}`;
    case 'call_step_required':
      return `Call your lead to complete the call step for your campaign. ${ctaLink}`;
    case 'inbound_missed_call':
      return `Return the call from your lead. ${ctaLink}`;
    case 'email_from_lead':
      return `Respond to the email from your lead. ${ctaLink}`;
    case 'sms_from_lead':
      return `Respond to the SMS from your lead. ${ctaLink}`;
    case 'new_lead_campaign_started':
    case 'hubspot_lead_created':
      return `Call your new lead. ${ctaLink}`;
    case 'service_calendar_setup':
    case 'service_calendar_due_date':
    case 'allocation_variance':
    case 'birthdays':
    case 'cash_variance':
    case 'advisor_requirements_needed':
    case 'transfer_in':
    case 'client_engagement':
    case 'outstanding_client_task':
    case 'overdue_advisor_task':
    case 'task_reassigned':
    case 'task_status_changed':
    case 'task_comment_created':
    case 'task_comment_mention':
    case 'household_comment_mention':
    case 'center_of_influence_rep_comment_mention':
    case 'subtask_assigned':
    case 'subtask_due_approaching':
    case 'rmds':
    case 'stagnant_prospect':
    case 'client_logins':
    case 'quarterly_report_failed':
    case 'quarterly_report_generated':
    case 'tax_filing_ready':
    case 'state_return_rejected':
    case 'state_return_accepted':
    case 'state_return_received':
    case 'federal_return_rejected':
    case 'federal_return_accepted':
    case 'federal_return_received':
    case 'client_completed_tax_enrollment':
    case 'client_uploaded_requested_file':
    case 'client_submitted_risk_questionnaire':
    case 'onboarding_complete':
    case 'service_calendar_task_assigned':
    case 'account_opened_or_funded':
    case 'client_portal_message_received':
    case 'task_created':
    case 'client_completed_task':
    case 'task_due_date_changed':
    case 'intake_invite_sent':
    case 'intake_invite_reminder_sent':
    case 'onboarding_invite_sent':
    case 'onboarding_invite_reminder_sent':
    case 'account_setup_confirmation_sent':
    case 'file_upload_request_sent':
    case 'risk_questionnaire_sent':
    case 'pre_review_check_in_sent':
    case 'service_calendar_invite_sent':
    case 'service_calendar_booking_reminder_sent':
    case 'meeting_booked_by_client':
    case 'meeting_canceled_or_rescheduled_by_client':
    case 'service_calendar_upcoming_meeting':
    case 'tax_filing_ready_sent_to_client':
    case 'tax_filing_reminder_to_client':
    case 'tax_filing_late_or_missed_deadline':
    case 'account_linked_or_aggregation_added':
    case 'large_withdrawal_detected':
    case 'beneficiary_or_ownership_change':
    case 'contribution_received':
    case 'docusign_outstanding':
    case 'meeting_prep_ready':
    case 'meeting_recap_ready':
    case 'meeting_transcript_needs_household':
      return '';
    default:
      return defaultGuard(alertType, '');
  }
}

export function generateGoogleCalendarEventURL(eventDate: Date, eventTitle: string, eventDescription: string) {
  const formattedEventDate: string = formatDateToYYYYMMDD(eventDate);

  return `https://calendar.google.com/calendar/u/0/r/eventedit?action=TEMPLATE&dates=${formattedEventDate}/${formattedEventDate}&details=${eventDescription}&text=${eventTitle}`;
}
